import React from 'react';
import { Box, Typography, Stack, Alert, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, SortSettingsModel, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { Refresh } from '@mui/icons-material';
import { WriteoffView } from '../../../../../services/WriteoffService/types';
import { WriteoffDocumentView } from '../../../../../services/WriteoffService/types/documentTypes';
import { useDocumentGridData, DocumentGridRow } from './hooks';
import { fileIconTemplate, fileNameTemplate, uploadByTemplate, uploadDateTemplate } from './templates';
import { gridContainerStyles, gridComponentStyles } from './styles';

interface DocumentsGridProps {
  writeoff: WriteoffView;
  documents: WriteoffDocumentView[];
  isLoading: boolean;
  error?: any;
  onDocumentView: (document: WriteoffDocumentView) => void;
  onRefresh: () => void;
  className?: string;
}

export const DocumentsGrid: React.FC<DocumentsGridProps> = ({
  writeoff,
  documents,
  isLoading,
  error,
  onDocumentView,
  onRefresh,
  className,
}) => {
  const gridData = useDocumentGridData(documents || []);

  const sortSettings: SortSettingsModel = {
    columns: [{ field: 'uploadDate', direction: 'Descending' }],
  };

  const pageSettings: PageSettingsModel = {
    pageSize: 12,
    pageSizes: [8, 12, 20, 30],
    currentPage: 1,
  };

  const onRowSelected = (args: RowSelectEventArgs) => {
    const selectedRow = args.data as DocumentGridRow;
    onDocumentView(selectedRow.document);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          ...gridContainerStyles,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 160,
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <CircularProgress size={32} />
        <Typography variant="body2">Loading documents...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={gridContainerStyles}>
        <Stack spacing={1.5}>
          <Alert severity="error" sx={{ fontSize: '0.875rem' }}>
            Failed to load documents: {error?.data?.message || 'An error occurred'}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title="Retry loading documents">
              <IconButton onClick={onRefresh} color="primary" size="small">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={gridContainerStyles}>
      <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          {gridData.length} document{gridData.length !== 1 ? 's' : ''} found
        </Typography>
        <Tooltip title="Refresh documents">
          <IconButton onClick={onRefresh} size="small" sx={{ p: 0.5 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={className}>
        <GridComponent
          dataSource={gridData}
          allowPaging={true}
          allowSorting={true}
          pageSettings={pageSettings}
          sortSettings={sortSettings}
          rowSelected={onRowSelected}
          height="320"
          width="1200"
          style={gridComponentStyles}
        >
          <ColumnsDirective>
            <ColumnDirective
              headerText=""
              width="50"
              textAlign="Center"
              allowSorting={false}
              allowFiltering={false}
              template={fileIconTemplate}
            />
            <ColumnDirective
              field="fileName"
              headerText="File Name"
              width="320"
              allowSorting={true}
              allowFiltering={true}
              template={fileNameTemplate}
            />
            <ColumnDirective
              field="uploadBy"
              headerText="Uploaded By"
              width="180"
              textAlign="Left"
              allowSorting={true}
              allowFiltering={true}
              template={uploadByTemplate}
            />
            <ColumnDirective
              field="uploadDateFormatted"
              headerText="Upload Date"
              width="200"
              textAlign="Left"
              allowSorting={true}
              allowFiltering={true}
              template={uploadDateTemplate}
            />
            <ColumnDirective
              field="uploadDate"
              headerText="Date"
              width="50"
              type="date"
              format="yMd"
              textAlign="Center"
              allowSorting={true}
              allowFiltering={true}
              visible={false}
            />
          </ColumnsDirective>
          <Inject services={[Page, Sort]} />
        </GridComponent>
      </Box>
    </Box>
  );
};

export default DocumentsGrid;
